/**
 * Theme Management Module
 *
 * Handles widget theme initialization, switching, and system preference detection.
 */

// Current theme state
let currentTheme = 'light';
let themeContainer = null;
let systemThemeListener = null;

/**
 * Initialize the theme module
 * @param {HTMLElement} container - The widget container element
 * @param {Object} tenantConfig - Tenant configuration
 */
export function initTheme(container, tenantConfig) {
    themeContainer = container;
    const themePreset = tenantConfig?.appearance?.theme_preset || 'light';

    if (themePreset === 'auto') {
        // Auto mode: follow system preference
        applySystemTheme();
        listenForSystemThemeChange();
    } else if (themePreset === 'dark') {
        setTheme('dark');
    } else {
        setTheme('light');
    }

    console.log('[FyncallTheme] Theme initialized:', themePreset);
    return currentTheme;
}

/**
 * Apply theme based on system preference
 */
export function applySystemTheme() {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    setTheme(prefersDark ? 'dark' : 'light');
}

/**
 * Listen for system theme changes
 * @param {Function} onThemeChange - Optional callback when theme changes
 */
export function listenForSystemThemeChange(onThemeChange) {
    if (!window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    // Remove existing listener if any
    if (systemThemeListener) {
        if (mediaQuery.removeEventListener) {
            mediaQuery.removeEventListener('change', systemThemeListener);
        } else if (mediaQuery.removeListener) {
            mediaQuery.removeListener(systemThemeListener);
        }
    }

    systemThemeListener = (e) => {
        const newTheme = e.matches ? 'dark' : 'light';
        setTheme(newTheme);
        console.log('[FyncallTheme] System theme changed:', newTheme);
        if (onThemeChange) {
            onThemeChange(newTheme);
        }
    };

    // Use addEventListener if available, otherwise use deprecated addListener
    if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', systemThemeListener);
    } else if (mediaQuery.addListener) {
        mediaQuery.addListener(systemThemeListener);
    }
}

/**
 * Set the widget theme
 * @param {string} theme - 'light' or 'dark'
 */
export function setTheme(theme) {
    currentTheme = theme;
    if (themeContainer) {
        themeContainer.dataset.theme = theme;
    }
    return currentTheme;
}

/**
 * Get the current theme
 * @returns {string} Current theme ('light' or 'dark')
 */
export function getTheme() {
    return currentTheme;
}

/**
 * Toggle between light and dark themes
 * @returns {string} The new theme
 */
export function toggleTheme() {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    return newTheme;
}

/**
 * Check if current theme is dark
 * @returns {boolean}
 */
export function isDarkTheme() {
    return currentTheme === 'dark';
}

/**
 * Cleanup theme listeners
 */
export function cleanupTheme() {
    if (systemThemeListener && window.matchMedia) {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        if (mediaQuery.removeEventListener) {
            mediaQuery.removeEventListener('change', systemThemeListener);
        } else if (mediaQuery.removeListener) {
            mediaQuery.removeListener(systemThemeListener);
        }
        systemThemeListener = null;
    }
    themeContainer = null;
}
